import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import { DICT } from "@/i18n/dictionaries";

export const SUPPORTED = ["nl", "en", "fr", "es"];
const DEFAULT_LANG = "en";

// Language persistence is session-scoped: a manual choice is kept for the whole
// browsing session (navigation, refresh, login/logout) via sessionStorage, but every
// NEW session starts in English. Any legacy cross-session localStorage value is
// removed once on init so it can never override the English default.
const STORAGE_KEY = "rep_lang";

// Locale used for date formatting per UI language.
export const DATE_LOCALE = { nl: "nl-BE", en: "en-GB", fr: "fr-BE", es: "es-ES" };

// Resolve the initial language: session choice > URL prefix > English default.
// Browser language/geolocation is intentionally NOT used — new sessions always start in EN.
export function detectInitialLang() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* legacy cross-session cleanup */ }
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch (e) { /* storage unavailable */ }
  const seg = (typeof window !== "undefined" ? window.location.pathname : "").split("/")[1];
  if (SUPPORTED.includes(seg)) return seg;
  return DEFAULT_LANG;
}

// Deep key lookup ("a.b.c") on a plain object.
function lookup(obj, key) {
  return key.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
}

const I18nContext = createContext({ lang: DEFAULT_LANG, setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    try { sessionStorage.setItem(STORAGE_KEY, next); } catch (e) { /* ignore */ }
    setLangState(next);
  }, []);

  // t(key, vars): resolves against the active language, falling back to EN, then the key itself.
  // Returns non-string values (arrays/objects) as-is so lists can be mapped directly.
  const t = useCallback((key, vars) => {
    let val = lookup(DICT[lang], key);
    if (val === undefined) val = lookup(DICT.en, key);
    if (val === undefined) return key;
    if (typeof val === "string" && vars) {
      return val.replace(/\{(\w+)\}/g, (m, name) => (vars[name] !== undefined ? String(vars[name]) : m));
    }
    return val;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

// Convenience hook returning just the translate function.
export function useT() {
  return useContext(I18nContext).t;
}


// Sets the browser tab title to a translated label ("<label> — Real Estate Pro").
// Used on auth/customer pages that don't mount SeoHead.
export function usePageTitle(key) {
  const { t, lang } = useI18n();
  useEffect(() => {
    if (key) document.title = `${t(key)} — Real Estate Pro`;
  }, [key, lang, t]);
}
